import { NavigationServiceV1 } from '@volkswagen-onehub/navigation-service';
import React from 'react';
import { UserInteractionServiceV1 } from '@volkswagen-onehub/user-interaction-service';
import { actionRedirect, normalizeToTimestamp } from './common-helpers';
import { COLOR_TOKEN, pastelGreen, pastelOrange } from './color-tokens';

const DAYS_UNTIL_EXPIRATION = 30;
const MAX_VALID_EXPIRATION_YEAR = 2100;

interface VoucherValidityInfo {
	color: string;
	message: string;
}

export class BenefitsHelper {
	public static getHuchaBonusValidityMessage(voucherExpirationDate: number | string | Date | null): string {
		return this._getHuchaBonusStatusValidityInfo(voucherExpirationDate).message;
	}

	public static getHuchaBonusStatusColor(voucherExpirationDate: number | string | Date | null): string {
		return this._getHuchaBonusStatusValidityInfo(voucherExpirationDate).color;
	}

	public static getVoucherPromotionColor(voucherExpirationDate: number | string | Date | null): string | null {
		if (!voucherExpirationDate || this.placeholderExpirationYear(voucherExpirationDate)) {
			return null;
		}

		const daysUntilExpiration = this._getDaysUntilExpiration(voucherExpirationDate);

		if (this._isExpired(voucherExpirationDate)) {
			return null;
		}

		return daysUntilExpiration < DAYS_UNTIL_EXPIRATION ? pastelOrange : pastelGreen;
	}

	public static getVoucherValidityMessage(voucherExpirationDate: number | string | Date | null): string | null {
		if (!voucherExpirationDate || this.placeholderExpirationYear(voucherExpirationDate)) {
			return null;
		}

		if (this._isExpired(voucherExpirationDate)) {
			return null;
		}

		return `Válido hasta el ${this._getDataValidityString(voucherExpirationDate)}`;
	}

	private static placeholderExpirationYear(dateTimestamp: number | string | Date): boolean {
		const timestamp = normalizeToTimestamp(dateTimestamp);
		return new Date(timestamp).getFullYear() > MAX_VALID_EXPIRATION_YEAR;
	}

	private static _getHuchaBonusStatusValidityInfo(
		voucherExpirationDate: number | string | Date | null,
	): VoucherValidityInfo {
		if (!voucherExpirationDate) {
			return { color: COLOR_TOKEN.signal.success, message: 'Disponible' };
		}

		const daysUntilExpiration = this._getDaysUntilExpiration(voucherExpirationDate);
		const expirationText = this._getDataValidityString(voucherExpirationDate);

		if (this._isExpired(voucherExpirationDate)) {
			return { color: COLOR_TOKEN.signal.error, message: 'Vencido' };
		}

		if (daysUntilExpiration < DAYS_UNTIL_EXPIRATION) {
			return { color: COLOR_TOKEN.signal.promo, message: `Válido hasta el ${expirationText}` };
		}

		return { color: COLOR_TOKEN.signal.success, message: `Válido hasta el ${expirationText}` };
	}

	private static _getDaysUntilExpiration(dateTimestamp: number | string | Date): number {
		const timestamp = normalizeToTimestamp(dateTimestamp);
		const millisecondsPerDay = 1000 * 60 * 60 * 24;

		return Math.ceil((timestamp - Date.now()) / millisecondsPerDay);
	}

	private static _isExpired(dateTimestamp: number | string | Date): boolean {
		const expirationDate = new Date(normalizeToTimestamp(dateTimestamp));
		const today = new Date();

		expirationDate.setHours(0, 0, 0, 0);
		today.setHours(0, 0, 0, 0);

		return expirationDate < today;
	}

	private static _getDataValidityString(dateTimestamp: number | string | Date): string {
		const timestamp = normalizeToTimestamp(dateTimestamp);
		const date: Date = new Date(timestamp);
		return date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear();
	}

	public static buildLiteralMarkdownElement(literal: string): JSX.Element {
		literal = literal.replace(/&ast;/g, '*');

		const processText = (text: string): JSX.Element => {
			const parts: JSX.Element[] = [];
			const boldRegex = /\*\*(.*?)\*\*/g;
			let lastIndex = 0;
			let match;

			while ((match = boldRegex.exec(text)) !== null) {
				if (match.index > lastIndex) {
					const beforeText = text.slice(lastIndex, match.index);
					parts.push(processUnderline(beforeText));
				}

				// Add bold text (process underlines inside)
				parts.push(<b key={match.index}>{processUnderline(match[1])}</b>);
				lastIndex = boldRegex.lastIndex;
			}

			if (lastIndex < text.length) {
				const remainingText = text.slice(lastIndex);
				parts.push(processUnderline(remainingText));
			}

			return <>{parts}</>;
		};

		const processUnderline = (text: string): JSX.Element => {
			const parts: JSX.Element[] = [];
			const underlineRegex = /__(.*?)__/g;
			let lastIndex = 0;
			let match;

			while ((match = underlineRegex.exec(text)) !== null) {
				if (match.index > lastIndex) {
					parts.push(processLinks(text.slice(lastIndex, match.index), `text-${lastIndex}`));
				}

				parts.push(<u key={match.index}>{processLinks(match[1], `u-${match.index}`)}</u>);
				lastIndex = underlineRegex.lastIndex;
			}

			if (lastIndex < text.length) {
				parts.push(processLinks(text.slice(lastIndex), `text-${lastIndex}`));
			}

			return parts.length > 0 ? <>{parts}</> : processLinks(text, 'span');
		};

		const processLinks = (text: string, keyPrefix: string): JSX.Element => {
			const parts: JSX.Element[] = [];
			const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
			let lastIndex = 0;
			let match;

			while ((match = linkRegex.exec(text)) !== null) {
				if (match.index > lastIndex) {
					parts.push(<span key={`${keyPrefix}-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>);
				}

				parts.push(
					<a
						key={`${keyPrefix}-${match.index}`}
						href={match[2]}
						target="_blank"
						rel="noopener noreferrer"
						style={{ color: 'inherit', textDecoration: 'underline' }}
					>
						{match[1]}
					</a>,
				);
				lastIndex = linkRegex.lastIndex;
			}

			if (lastIndex < text.length) {
				parts.push(<span key={`${keyPrefix}-${lastIndex}`}>{text.slice(lastIndex)}</span>);
			}

			return parts.length > 0 ? <>{parts}</> : <span>{text}</span>;
		};

		const lines = literal.split('\n');
		const elements = lines.map((line, index) => (
			<React.Fragment key={index}>
				{processText(line)}
				{index < lines.length - 1 && <br />}
			</React.Fragment>
		));

		return <>{elements}</>;
	}
}

export const vwBenefitsRedirect = (
	benefitSrc: string | undefined,
	navigationService: NavigationServiceV1,
	userInteractionService?: UserInteractionServiceV1,
) => {
	const regex = /^(http(s):\/\/.)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/;
	if (benefitSrc) {
		if (regex.test(benefitSrc)) {
			window.open(benefitSrc, '_blank'); // Open in a new tab
		} else if (userInteractionService && benefitSrc.includes('action:')) {
			actionRedirect(userInteractionService, benefitSrc);
		} else {
			const target = navigationService.navigateTo(benefitSrc, {});
			if (target) {
				target.push();
			} else {
				if (benefitSrc) {
					console.log('detail page not created');
				} else {
					console.log('no detail page provided');
				}
			}
		}
	} else {
		console.log('no detail page provided');
	}
};
